// Analyse qualité d'un logo à l'import : plage de largeurs imprimables.
// - minCm : plus petite largeur où plus aucun trait ne passe sous 0,5 mm.
//   null = jamais de traits fins ; Infinity = trop fins à toutes les tailles.
//   20.10.2026 (Daniel, logo « test » : « agrandir à 48,5 cm, c'est pas possible ») : hasThinStrokes n'est PAS
//   monotone sur un dessin à éclaboussures — une goutte « poussière » à 10 cm devient un « vrai trait » à 40 cm
//   (le filtre des vrais traits est en mm, le dessin, lui, grandit). La recherche binaire sautait alors jusqu'à
//   la laize. On BALAIE désormais les largeurs en montant et on s'arrête à la première propre : c'est elle que
//   l'on annonce, et c'est bien la plus petite largeur où le logo s'imprime sans trait fin.
// - maxCm : plus grande largeur où les DPI restent >= minDpi (raster uniquement,
//   null = vectoriel, netteté illimitée).
import { hasThinStrokes } from './thinlines';

export interface QualityInfo {
	/** data URL analysée (invalide la fiche quand le logo change : upscale, correction) */
	src: string;
	pxW: number;
	pxH: number;
	minCm: number | null;
	maxCm: number | null;
}

const cache = new Map<string, Promise<QualityInfo>>();

// largeurs testées (cm) : serrées là où les logos se commandent, plus lâches au-delà
const SCAN_CM = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 25, 30, 35, 40, 45, 50, 55, 60, 70, 80, 100];

const loadDims = (src: string) =>
	new Promise<{ w: number; h: number }>((resolve, reject) => {
		const im = new Image();
		im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
		im.onerror = () => reject(new Error('image load failed'));
		im.src = src;
	});

export function analyzeQuality(
	src: string,
	kind: string,
	aspect: number,
	maxWidthCm: number,
	minDpi: number,
	/** largeur actuelle du logo (cm) : toujours testée telle quelle, pour que la plage annoncée ne contredise jamais
	 *  l'alerte en direct ni une correction faite à cette largeur (grille seule : 15 cm épaissi → « dès 35 cm ») */
	widthCm?: number,
): Promise<QualityInfo> {
	const wNow = widthCm && widthCm >= 1 ? Math.round(widthCm * 10) / 10 : 0;
	const key = `${kind}|${aspect}|${maxWidthCm}|${minDpi}|${wNow}|${src}`;
	let p = cache.get(key);
	if (!p) {
		p = (async () => {
			const { w, h } = await loadDims(src);
			// 07.10.2026 (revue Neo) : un PDF est rendu en image (~2000 px) à l'import → même limite de dpi qu'un raster
			const maxCm = kind !== 'svg' ? Math.floor((w / minDpi) * 2.54 * 10) / 10 : null;
			const hi0 = Math.max(2, Math.round(maxWidthCm * 2) / 2);
			// hauteur arrondie au mm comme partout ailleurs (setWidth, alerte live) :
			// sans ça le minCm annoncé peut encore déclencher l'alerte traits fins
			const thinAt = (wCm: number) => hasThinStrokes(src, wCm, Math.round((wCm / aspect) * 10) / 10);
			// largeurs candidates : la grille jusqu'à la laize, la laize elle-même, et la limite de netteté (maxCm) —
			// c'est à cette largeur que la correction épaissit quand « traits ≥ X cm mais dpi ≤ Y cm », elle doit donc
			// être testée telle quelle
			const cands = new Set<number>(SCAN_CM.filter(c => c <= hi0));
			cands.add(hi0);
			if (maxCm !== null && maxCm >= 1 && maxCm <= hi0) cands.add(Math.round(maxCm * 10) / 10);
			if (wNow && wNow <= hi0) cands.add(wNow);
			const widths = [...cands].sort((a, b) => a - b);
			let minCm: number | null = Infinity;
			for (const wCm of widths) {
				// rendre la main entre deux largeurs : sinon tout le balayage tient dans une seule tâche et l'écran se fige
				await new Promise(r => setTimeout(r, 0));
				if (!(await thinAt(wCm))) {
					minCm = wCm === 1 ? null : wCm;
					break;
				}
			}
			return { src, pxW: w, pxH: h, minCm, maxCm };
		})();
		cache.set(key, p);
		if (cache.size > 100) cache.delete(cache.keys().next().value as string);
	}
	return p;
}

export const fmtCm = (v: number): string => String(Math.round(v * 10) / 10).replace('.', ',');
